
import React from "react";
import * as Hooks from "../hooks";
import { i18nManager } from "../i18n";
import infoImage from "../assets/images/info.svg";
import * as ReactRouterDom from "react-router-dom";
import { NavLink, useLocation } from "react-router-dom";
import { CacheManager, Schemes, Utils } from "../utils";
import { BoolHelper, Button, Checkbox, Classname, DateHelper, Dropdown, FormControl, NoseurObject, ObjectHelper, Popover, PopoverManageRef, ResizeSensor, Scheme, Subscriber, TextInput, useScreenDimension, useSubscription } from "@rivtn/noseur";
import { ApiResponseData, BaseService, CountryService, ICountry, IMember, INotification, IProfile, MemberNotificationService, MemberService, OmneHostStatus, SanitizedResponse } from "../services";
import { ConfirmDialog, Siren } from "./shared/ConfirmDialog";
import LoadingGif from "../assets/images/loading_circle.gif";
import StatefulLink from "./shared/StatefulLink";
import Dashboard from "./sections/dashboard/Dashboard";
import TournamentFeed from "./shared/TournamentFeed";
import Teams from "./sections/teams/Teams";
import AccountSettings from "./sections/account_settings/AccountSettings";
import { Elements } from "./shared/Elements";
import { Notifier } from "./shared/Notifiier";
import HostTournament from "./sections/host/HostTournament";
import Tournaments from "./sections/tournaments/Tournaments";
import Earnings from "./sections/earnings/Earnings";
import { FeatureFlagEvent, useFeatureFlags } from "@syllektis/oya-gbewa-react";

export interface CompoundProps {
    profile: IMember;
}

function Compound(props: CompoundProps) {

    const location = useLocation();
    const labels = i18nManager.Labels;
    const { width } = useScreenDimension();
    const memberService = MemberService.getInstance();
    const countryService = CountryService.getInstance();
    const [themeMode, setThemeMode] = Hooks.useThemeMode();
    const popoverControlEls = React.useRef<NoseurObject<any>>({});
    const [countries, setCountries] = React.useState<ICountry[]>();
    const [profile, setProfile] = React.useState<IMember>(props.profile);
    const profilePopOverManageRef = React.useRef<PopoverManageRef>(null);
    const mobileMenuPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const memberNotificationService = MemberNotificationService.getInstance();
    const notificationPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const isTournamentPage = Utils.containsAny(location.pathname, ["/tournaments"]);
    const [unreadMessagesCount, setUnreadMessagesCount] = React.useState<number>(0);
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const janusLaunariusProfile = CacheManager.get(CacheManager.PROFILE_KEY) as IProfile;
    const [sidebarType, setSidebarType] = React.useState<"landing" | "account">("account");
    const [ongoingTournamentsCount, setOngoingTournamentsCount] = React.useState<number>(0);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = React.useState<number>(0);
    const [memberNotifications, setMemberNotifications] = React.useState<INotification[]>([]);
    const [collapseSidebar, setCollapseSidebar] = React.useState<boolean>(Utils.getSetting("csb", false));
    const [showTournamentFeed, setShowTournamentFeed] = React.useState<boolean>(Utils.getSetting("stf", true));
    const [tournamentFilterCollapsed, setTournamentFilterCollapsed] = React.useState<boolean>(Utils.getSetting("ctf", false));
    const { languageToggle, darkModeToggle, enableTournamentFeed } = useFeatureFlags({
        query: { email: profile.user_detail.email, plan: profile.plan, country: profile.country_code, environment: process.env.REACT_APP_ENV }
    });
    useSubscription("OMNEHOST.PROFILE.UPDATED", setProfile);
    useSubscription("OMNEHOST.PAGE.TOGGLE_TFEED", setShowTournamentFeed);
    useSubscription("OMNEHOST.PAGE.COLLAPSE_SIDEBAR", setCollapseSidebar);
    useSubscription(Subscriber.KEYS.SECONDARY_DATA, (secondaryData: any) => {
        let notificationCount = secondaryData?.un_reads?.notifications ?? unreadNotificationsCount;
        if (secondaryData?.un_reads?.notifications > 9) notificationCount = "9+";
        setUnreadNotificationsCount(notificationCount);
        setOngoingTournamentsCount(secondaryData?.ongoing_tournaments ?? 0);
    });

    React.useEffect(() => {
        fetchNotifications();
        countryService.queryCountries({ name: "" }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!.content);
        }).catch(BaseService.reportError);
    }, []);
    React.useEffect(() => {
        setSidebarType(location.pathname.includes("/account/") ? "account" : "landing");
    }, [location]);

    React.useEffect(() => {
        const route = window.location.pathname;
        if (route !== "/verify") return;
        const payload = {
            member_id: searchParams.get("u"),
            email_factor: {
                value: searchParams.get("c"),
                factor_id: searchParams.get("f"),
            }
        };
        const dialog = ConfirmDialog({
            noControl: true,
            title: labels.compound.landing.verify_email.verify_your_profile,
            content: (<div style={{ margin: "90px 110px" }}>
                <img alt={"verifying profile"} src={LoadingGif} style={{ width: 100 }} />
            </div>),
        });
        dialog.show(() => {
            memberService.verifyEmail(payload).then((_) => {
                memberService.getProfile(true).then(({ sanitized }: SanitizedResponse<IMember>) => {
                    dialog.hide(() => {
                        setProfile(sanitized);
                        Siren.alert(labels.compound.landing.verify_email.verify_successful);
                        window.location.replace(window.location.origin);
                    });
                }).catch(BaseService.reportError)
            }).catch((err: any) => {
                dialog.update({
                    content: (<div className="ver-pro-error">
                        <span>{labels.compound.landing.verify_email.error_occur}</span>
                        <span className="error">{labels.common.error}: {err.errorMessage}</span>
                        {(err.response?.status !== 403) && (<StatefulLink label={labels.compound.landing.verify_email.resend_link} onClick={async (done: () => Promise<void>) => {
                            memberService.requestAccountVerificationEmail().then((_) => {
                                dialog.hide();
                                window.location.replace(window.location.origin); // TODO don't refresh just change the route
                                Siren.alert(labels.compound.landing.verify_email.verify_link_sent);
                            }).catch(BaseService.reportError).finally(() => done());
                        }} />)}
                        {(err.response?.status === 403) && (<span className="link" onClick={() => {
                            dialog.hide();
                            window.location.replace(window.location.origin); // TODO don't refresh just change the route
                        }}>{labels.common.close}</span>)}
                    </div>),
                } as any);
                BaseService.reportError(err);
            });
        });
    }, []);

    return (<div className={Classname.build("compound", themeMode)}>
        <ResizeSensor minDimension={{ width: 900 }}>
            {renderSidebar()}
        </ResizeSensor>
        <div className="main-layout">
            <div className="top-nav">
                <ResizeSensor maxDimension={{ width: 900 }}>
                    <Button iconOnly borderless leftIcon="fa fa-bars" scheme={Schemes.OMNEHOST} style={{ marginLeft: 10 }} ref={(el) => { popoverControlEls.current["mobile-menu"] = el; }} onClick={(e: any) => {
                        fetchNotifications();
                        mobileMenuPopOverManageRef.current?.toggle(e, popoverControlEls.current["mobile-menu"]);
                    }} />
                    <Popover manageRef={mobileMenuPopOverManageRef} className={Classname.build("noseur-popover-b np-b-min pop-fs compound", themeMode)} style={{ marginTop: 60, minWidth: 0, width: "100dvw !important", height: "100dvh !important", left: 0, minHeight: "100dvh !important" }}>
                        {renderSidebar(() => {
                            mobileMenuPopOverManageRef?.current?.hide();
                        })}
                    </Popover>
                </ResizeSensor>
                <FormControl borderless scheme={Schemes.OMNEHOST} style={{ paddingLeft: 20, flex: 1, visibility: "hidden" }} leftContent={<i className="fa fa-search" style={{ color: "var(--textColorFaint)" }} />}>
                    <TextInput noStyle style={{ margin: 10 }} placeholder={labels.compound.top_navbar.search} />
                </FormControl>
                <div className="actions">
                    {(languageToggle) && (<Dropdown noToggleIcons noSelectionIcon borderless scheme={Schemes.OMNEHOST} options={i18nManager.Languages}
                        style={{ width: 65, fontWeight: "bold" }}
                        selectedOptionIndex={i18nManager.Languages.findIndex(l => l.value === i18nManager.ActiveLanguage)}
                        onSelectOption={(option) => {
                            i18nManager.switchLanguage(option.value);
                            return true;
                        }} optionsClassName="language-dd" popoverProps={{ className: themeMode }}
                        translateSelectionLabel={(_, option) => {
                            return (option.short_name);
                        }}
                        popoverHeaderTemplate={() => (<div style={{ margin: "5px 10px", fontSize: 12, color: "var(--textColorFaint)" }}>{labels.compound.top_navbar.select_a_language}</div>)} />)}
                    <div className="noseur-badge-overlay" style={{ cursor: "pointer" }}>
                        {(!!unreadMessagesCount) && (<span className="noseur-badge noseur-danger noseur-no-border">{unreadMessagesCount}</span>)}
                        <Button leftIcon="fa fa-message" textOnly rounded scheme={Schemes.OMNEHOST} />
                    </div>
                    <div className="noseur-badge-overlay" style={{ cursor: "pointer" }} ref={(el) => { popoverControlEls.current["notification"] = el; }}
                        onClick={(e: any) => {
                            fetchNotifications();
                            notificationPopOverManageRef.current?.toggle(e, popoverControlEls.current["notification"]);
                        }}>
                        {(!!unreadNotificationsCount) && (<span className="noseur-badge noseur-danger noseur-no-border">{unreadNotificationsCount}</span>)}
                        <Button leftIcon="fa fa-bell" textOnly rounded scheme={Schemes.OMNEHOST} />
                    </div>
                    <Button className="profile-dropdown" scheme={Schemes.OMNEHOST} ref={(el) => { popoverControlEls.current["profile"] = el; }}
                        onClick={(e: any) => profilePopOverManageRef.current?.toggle(e, popoverControlEls.current["profile"])}>
                        <img src={profile?.profile_picture_url ?? janusLaunariusProfile.profile_picture_location} alt={profile?.username ?? janusLaunariusProfile.email} />
                        <i className="fa fa-angle-down" />
                    </Button>
                </div>
            </div>
            <div className="main-content">
                <div className="container">
                    <div>
                        {(profile?.status === OmneHostStatus.UNVERIFIED) && (<div className="verify-profile">
                            {(location.pathname === "/") && (<span className="t">{labels.compound.landing.hello}, {profile.username}</span>)}
                            <span className="d" style={location.pathname !== "/" ? { maxWidth: "unset" } : {}}>
                                {<b>{labels.compound.landing.hello}, {profile.username}!, </b>}
                                {ObjectHelper.expandStringTemplate((location.pathname === "/"
                                    ? labels.compound.landing.verify_email.verify_profile : labels.compound.landing.verify_email.verify_profile_short), janusLaunariusProfile, { chop: "$" })}
                                <StatefulLink label={labels.compound.landing.verify_email.resend} onClick={async (done: () => Promise<void>) => {
                                    memberService.requestAccountVerificationEmail().then((_) => {
                                        Siren.alert(labels.compound.landing.verify_email.verify_link_sent);
                                    }).catch(BaseService.reportError).finally(() => done());
                                }} />
                            </span>
                        </div>)}
                        {profile && (<ReactRouterDom.Routes>
                            <ReactRouterDom.Route path="/*" element={<Dashboard profile={profile} />} />
                            <ReactRouterDom.Route path="/teams/*" element={<Teams profile={profile} />} />
                            <ReactRouterDom.Route path="/earnings/*" element={<Earnings profile={profile} />} />
                            <ReactRouterDom.Route path="/account/*" element={<AccountSettings profile={profile} />} />
                            <ReactRouterDom.Route path="/tournaments/*" element={<Tournaments profile={profile} />} />
                            <ReactRouterDom.Route path="/hosts/*" element={<HostTournament profile={profile} />} />
                        </ReactRouterDom.Routes>)}
                    </div>
                    {(enableTournamentFeed && showTournamentFeed && !(Utils.containsAny(location.pathname, ["/earnings", "/tournaments", "/hosts", "/analytics"]) && !location.pathname.includes("/withdrawal"))) && (
                        <ResizeSensor minDimension={{ width: 1200 }}><TournamentFeed profile={profile} /></ResizeSensor>
                    )}
                </div>
            </div>
        </div>

        <Popover manageRef={notificationPopOverManageRef} className={Classname.build("noseur-popover-b np-b-min notifis", themeMode)} style={{ marginTop: 55, minWidth: 380 }}>
            <div className="topper">
                <span>{labels.compound.top_navbar.popover.notification.notifications}</span>
                <span className="link-hv" onClick={() => {
                    notificationPopOverManageRef.current?.hide();
                    memberNotificationService.markAllAsRead().then(({ }: SanitizedResponse<void>) => {
                        Siren.alert(labels.compound.top_navbar.popover.notification.mark_all_read_success);
                        fetchNotifications();
                    }).catch(BaseService.reportError);
                }}>{labels.compound.top_navbar.popover.notification.mark_all_as_read}</span>
            </div>
            <div className="notifs">
                {memberNotifications?.map((memberNotification) => Notifier.buildPopoverNotification(memberNotificationService, memberNotification, fetchNotifications))}
            </div>
            {!memberNotifications?.length && (<div className="no-notifs">
                <img src={infoImage} alt="no notifications" />
                <span>{labels.compound.top_navbar.popover.notification.no_notifications}</span>
                <StatefulLink label={i18nManager.Labels.common.refresh} onClick={async (done: () => Promise<void>) => {
                    fetchNotifications(done);
                }} />
            </div>)}
            <div className="footing">
                <NavLink className="link-hv base" to="/account/notifications" onClick={() => notificationPopOverManageRef.current?.hide()}>
                    {labels.compound.top_navbar.popover.notification.see_all_notifications}
                </NavLink>
                <span></span>
            </div>
        </Popover>
        <Popover dismissOnClick manageRef={profilePopOverManageRef} className={Classname.build("noseur-popover-b np-b-min", themeMode)} style={{ marginTop: 55 }}>
            <div className="profi">
                <img src={profile?.profile_picture_url ?? janusLaunariusProfile.profile_picture_location} alt={profile?.username ?? janusLaunariusProfile.email} />
                <NavLink to={Utils.buildPublicUrl("PROFILE", profile.username)} target="_blank" className="brief">
                    <span className="u">{profile?.username}</span>
                    <span className="l">{labels.compound.top_navbar.popover.profile.view_profile}</span>
                </NavLink>
            </div>
            <NavLink to={"/account/edit-profile"}>
                <i className="fa fa-pen" />
                <span>{labels.compound.top_navbar.popover.profile.edit_profile}</span>
            </NavLink>
            <NavLink to={"/account/manage-plan"}>
                <i className="fa fa-star" style={{ color: "gold" }} />
                <span>{labels.compound.top_navbar.popover.profile.change_plan}</span>
            </NavLink>
            <NavLink to={"/account/website/general"}>
                <i className="fa fa-globe" />
                <span>{labels.compound.top_navbar.popover.profile.manage_website}</span>
            </NavLink>
            {(darkModeToggle) && (<div className="toggle">
                <div>
                    <i className="fa fa-pen" />
                    <span>{labels.compound.top_navbar.popover.profile.dark_mode}</span>
                </div>
                <Checkbox defaultChecked={themeMode === "dark"} onChange={(e: any) => setThemeMode(e.checked ? "dark" : "light")} />
            </div>)}
            {(enableTournamentFeed) && (<div className="toggle">
                <div>
                    <i className="fa fa-bars" />
                    <span>{labels.compound.top_navbar.popover.profile.tournament_feed}</span>
                </div>
                <Checkbox defaultChecked={showTournamentFeed} onChange={() => {
                    const newValue = !showTournamentFeed;
                    setShowTournamentFeed(newValue);
                    Utils.updateSettings("stf", newValue);
                }} />
            </div>)}
            <div className="toggle" onClick={() => {
                const newValue = !collapseSidebar;
                setCollapseSidebar(newValue);
                Utils.updateSettings("csb", newValue)
            }}>
                <div>
                    <i className="fa fa-bars" />
                    <span>{collapseSidebar ? labels.compound.top_navbar.popover.profile.expand_sidebar : labels.compound.top_navbar.popover.profile.collapse_sidebar}</span>
                </div>
            </div>
            <NavLink to={"https://support.rivtn.com/?platform=omnehost"}>
                <i className="fa fa-headphones" />
                <span>{labels.compound.top_navbar.popover.profile.help_and_support}</span>
            </NavLink>
            <div onClick={() => {
                localStorage.clear();
                window.location.href = process.env.REACT_APP_OMNEHOST_HOMEPAGE ?? "https://omnehost.com";
            }}>
                <i className="fa fa-sign-out" />
                <span style={{ color: "#FF7171" }}>{labels.compound.top_navbar.popover.profile.signout}</span>
            </div>
        </Popover>
    </div>);

    function renderSidebar(onItemClicked?: Function) {
        return (<div className="sidebar">
            <div className="logo" onClick={() => {
                if (onItemClicked) {
                    onItemClicked();
                    return;
                }
                const newValue = !collapseSidebar;
                setCollapseSidebar(newValue);
                Utils.updateSettings("csb", newValue)
            }}>
                <div className="d">
                    <img src={labels.logo} alt={labels.name} />
                    {(!collapseSidebar || width < 800) && (<div>
                        <span>OMNE</span>
                        <span>HOST</span>
                    </div>)}
                </div>
                <ResizeSensor maxDimension={{ width: 900 }}>
                    <Button iconOnly borderless outlined leftIcon="fa fa-times" scheme={Scheme.DARK} style={{ marginLeft: 10 }} ref={(el) => { popoverControlEls.current["mobile-menu"] = el; }} onClick={(e: any) => {
                        onItemClicked?.();
                    }} />
                </ResizeSensor>
            </div>
            <div className={Classname.build("menu", sidebarType)}>
                {sidebarType === "landing" && (labels.compound.menu_locations.filter((ml) => {
                    if (!isTournamentPage || tournamentFilterCollapsed) return true;
                    return BoolHelper.equalsAny(ml.location, ["/", "/tournaments"]);
                }).map((l) => (<NavLink key={l.label} to={l.location} className={"base"} onClick={() => onItemClicked?.()}>
                    <i className={l.icon} />
                    {(!collapseSidebar || width < 800) && (<span>{l.label}</span>)}
                    {((!collapseSidebar || width < 800) && ongoingTournamentsCount > 0 && l.location === "/tournaments") && (Elements.buildPulsar({ marginLeft: 15 }))}
                </NavLink>)))}
                {!collapseSidebar && isTournamentPage && (<div className="tournament-filter">
                    <div className="controls">
                        <span className="f">{labels.tournaments.filter.filter}</span>
                        <span className="link danger">{labels.tournaments.filter.clear}</span>
                    </div>
                    <div className="filter">
                        {Elements.buildConfirmDialogForm(labels.tournaments.filter.form.filter((_, index) => {
                            return !tournamentFilterCollapsed || index < 2;
                        }), (name: string, value: any, searching: any) => {
                            /*dialogErrorRef.current!.innerText = " ";
                            if (searching && name === "country_code") {
                                countryService.queryCountries({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
                                    value.manageRef?.changeOptions(apiResponse.data!.content?.map((country) => ({ value: country.code, label: country.name })));
                                }).catch(BaseService.reportError);
                            }
                            payload[name] = value;*/
                            return false;
                        }, /*payload*/undefined, undefined, { marginTop: 10 }, {
                            country: countries?.map((country) => ({ value: country.code, label: country.name }))
                        }, { marginTop: 10 })}
                    </div>
                    <span className="col-exp" onClick={() => {
                        const newValue = !tournamentFilterCollapsed;
                        setTournamentFilterCollapsed(newValue);
                        Utils.updateSettings("ctf", newValue)
                    }}>{tournamentFilterCollapsed ? labels.tournaments.filter.show_x_more.replace("${count}", "" + (labels.tournaments.filter.form.length - 2)) : labels.tournaments.filter.collapse_filter}</span>
                    <div className="familiar-orgs">
                        <span>{labels.tournaments.filter.familiar_orgs}</span>
                        <div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Karri Secondst</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Secondary Skate Platform</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Karri Secondst</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Secondary Skate Platform</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Soldier Hubs</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Local Baseball League LBL</span>
                            </div>
                            <div>
                                <img alt="org name" src="https://png.pngtree.com/png-vector/20230822/ourmid/pngtree-football-team-logo-vector-png-image_9189255.png" />
                                <span>Secondary Skate Platform</span>
                            </div>
                        </div>
                    </div>
                </div>)}
                {(sidebarType === "account") && (<>
                    <NavLink to={"/"} className="back" onClick={() => setSidebarType("landing")}>
                        <i className="fa fa-arrow-left" />
                        {!collapseSidebar && (<span>{labels.compound.account_settings.menu.menu}</span>)}
                    </NavLink>
                </>)}
                {(sidebarType === "account" && !collapseSidebar) && (<>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.notifications.location} style={{ marginLeft: 0, marginTop: 15 }}>{labels.compound.account_settings.menu.notifications.label}</NavLink>
                    <span className="title">{labels.compound.account_settings.menu.plan_and_billing}</span>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.manage_plan.location}>{labels.compound.account_settings.menu.manage_plan.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.billing.location}>{labels.compound.account_settings.menu.billing.label}</NavLink>
                    <span className="title">{labels.compound.account_settings.menu.manage_account}</span>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.edit_profile.location}>{labels.compound.account_settings.menu.edit_profile.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.interest_and_ai.location}>{labels.compound.account_settings.menu.interest_and_ai.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.social_accounts.location}>{labels.compound.account_settings.menu.social_accounts.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.sessions.location}>{labels.compound.account_settings.menu.sessions.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.connected_hosts.location}>{labels.compound.account_settings.menu.connected_hosts.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.manage_data.location}>{labels.compound.account_settings.menu.manage_data.label}</NavLink>
                    <span className="title">{labels.compound.account_settings.menu.profile_website}</span>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.general.location}>{labels.compound.account_settings.menu.general.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.media.location}>{labels.compound.account_settings.menu.media.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.home.location}>{labels.compound.account_settings.menu.home.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.merch.location}>{labels.compound.account_settings.menu.merch.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.achievements.location}>{labels.compound.account_settings.menu.achievements.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.gallery.location}>{labels.compound.account_settings.menu.gallery.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.schedule.location}>{labels.compound.account_settings.menu.schedule.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.team_history.location}>{labels.compound.account_settings.menu.team_history.label}</NavLink>
                    <NavLink onClick={() => onItemClicked?.()} to={labels.compound.account_settings.menu.design.location}>{labels.compound.account_settings.menu.design.label}</NavLink>
                </>)}
            </div>
            {!isTournamentPage && !collapseSidebar && (<div className="quick-links">
                {labels.compound.quick_locations.map((l) => (<a key={l.label} href={l.location} rel="noreferrer" target="_blank" className="skn">{l.label}</a>))}
            </div>)}
        </div>);
    }

    function fetchNotifications(done?: () => Promise<void>) {
        memberNotificationService.queryNotifications({ size: 20 }).then(({ sanitized }: SanitizedResponse<ApiResponseData<INotification>>) => {
            setMemberNotifications(sanitized?.content ?? []);
            done?.();
        }).catch(BaseService.reportError);
    }

}

export default Compound;
